import { formatDate, isTodaysDate } from "@/lib/utils";
import { SummaryDetailBox } from "./SummaryDetailBox";
import { SummaryDetailMetric } from "./SummaryDetailMetric";

type Theme = "red" | "green" | "amber" | "default";

interface PipelineStatistics {
  appsTotal?: number;
  warmAppsTotal?: number;
  allocatedOnSelectedDayTotal?: number;
  leadsToday?: number | null;
  leadsTotal?: number;
  predictedToDocs: number;
  docsToday: number;
  totalReDoc?: number | null;
  gapDocsOut: number;
  gapReadyToGo: number;
  gapPayout: number;
  gapTotal: number;
  predictedToPay: number;
  paidToday: number;
}

interface TopLevelStatsBoxProps {
  statistics: PipelineStatistics | null;
  selectedDate: Date;
}

const THEMES = {
  RED: "red" as const,
  GREEN: "green" as const,
  AMBER: "amber" as const,
  DEFAULT: "default" as const,
} as const;

const getTodayTheme = (today: number | undefined, predicted: number | undefined): Theme => {
  if (!today || !predicted) return THEMES.DEFAULT;

  if (today > predicted) return THEMES.GREEN;
  if (today < predicted) return THEMES.AMBER;
  return THEMES.DEFAULT;
};

const getPredictionTheme = (predicted: number | undefined): Theme => {
  return predicted === 0 ? THEMES.RED : THEMES.DEFAULT;
};

export const TopLevelStatsBox = ({ statistics, selectedDate }: TopLevelStatsBoxProps) => {
  const { predictedToDocs, docsToday, predictedToPay, paidToday } = statistics || {};
  const dateLabel = isTodaysDate(selectedDate) ? "Today" : formatDate(selectedDate);

  const predictedToDocsTheme = getPredictionTheme(predictedToDocs);
  const predictedToPayTheme = getPredictionTheme(predictedToPay);
  const docsTodayTheme = getTodayTheme(docsToday, predictedToDocs);
  const paidTodayTheme = getTodayTheme(paidToday, predictedToPay);

  const displayLeads = statistics?.leadsToday !== undefined && statistics?.leadsToday !== null;
  const displayCustomers =
    statistics?.appsTotal !== undefined ||
    statistics?.warmAppsTotal !== undefined ||
    statistics?.allocatedOnSelectedDayTotal !== undefined;

  return (
    <div className="grid gap-3 border-b border-solid border-slate-200 bg-slate-50 p-3 pt-5 md:grid-cols-2 xl:grid-cols-5">
      {displayLeads && (
        <SummaryDetailBox label="Leads" colSpan={1}>
          <SummaryDetailMetric label={dateLabel} value={statistics?.leadsToday ?? undefined} />
        </SummaryDetailBox>
      )}

      {displayCustomers && (
        <SummaryDetailBox label="Customers" colSpan={1}>
          <SummaryDetailMetric label={dateLabel} value={statistics?.allocatedOnSelectedDayTotal} />
          <SummaryDetailMetric label="Warm" value={statistics?.warmAppsTotal} />
          <SummaryDetailMetric label="Total" value={statistics?.appsTotal} />
        </SummaryDetailBox>
      )}

      <SummaryDetailBox label="Docs" colSpan={1}>
        <SummaryDetailMetric label="Predicted" value={statistics?.predictedToDocs} theme={predictedToDocsTheme} />
        <SummaryDetailMetric label={dateLabel} value={statistics?.docsToday} theme={docsTodayTheme} />
        {statistics?.totalReDoc !== undefined && statistics?.totalReDoc !== null && (
          <SummaryDetailMetric label="Redocs" value={statistics?.totalReDoc} />
        )}
      </SummaryDetailBox>

      <SummaryDetailBox label="Gap" colSpan={2}>
        <SummaryDetailMetric label="WIP" value={statistics?.gapDocsOut} />
        <SummaryDetailMetric label="Ready to Go" value={statistics?.gapReadyToGo} />
        <SummaryDetailMetric label="Payout" value={statistics?.gapPayout} />
        <SummaryDetailMetric label="Total" value={statistics?.gapTotal} />
      </SummaryDetailBox>

      <SummaryDetailBox label="Paid" colSpan={1}>
        <SummaryDetailMetric label="Predicted" value={statistics?.predictedToPay} theme={predictedToPayTheme} />
        <SummaryDetailMetric label={dateLabel} value={statistics?.paidToday} theme={paidTodayTheme} />
      </SummaryDetailBox>
    </div>
  );
};
